import type { ComputedRef, Ref } from 'vue'
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import useEventsBus from './eventBus'
import Article from '@/models/article'
import type { Price } from '@/models/articlePrice'
import { type FilterCriteria, FilterCriteriaMode } from '@/models/filterCriteria'
import MyArticle from '@/models/myArticle'
import type FavoriteTag from '@/models/favoriteTag'
import SimpleFavoriteTag from '@/models/simpleFavoriteTag'
import appConfig from '@/services/appConfig'
import { useBrowseByStore } from '@/store/browseBy'
import { useUserStore } from '@/store/userData'
import { ARTICLES_LIMIT } from '@/services/db'
import { appConstants, privileges } from '@/models/constants'
import { useArticleStore } from '@/store/article'
import utils, { isDefined } from '@/services/utils'
import net from '@/services/net'
import type LinkedCustomer from '@/models/linkedCustomer'
import { useNotificationStore } from '@/store/notification'

// export function useLiveArticlesObs(criteria: Ref<FilterCriteria[] | undefined>, onLoading: (fullReload: boolean) => void, onChanged: (fullReload: boolean) => void) {
//   const myData = ref<MyArticle[]>([])
//   const myDataIndex = computed(() => myData.value.reduce((acc, curr, index) => { acc[curr.Id] = index; return acc }, {}))
//   const userStore = useUserStore()
//   const basePart = `idb://${appConfig.T1Env}`
//   const listener = async (changedParts: ObservabilitySet) => {
//     let updateFavs = false

//     console.log('Changed parts', changedParts)
//     for (const part in changedParts) {
//       if (part === `${basePart}/favoriteTags/Articles`) {
//         // Favorites have changed
//         updateFavs = true
//       }
//     }

//     if (updateFavs) {
//       // Reload articles favorites
//       const allFavs = await appConfig.DB!.myFavorites.where('[CatalogCode+CreatedByUserName+Status]').equals([userStore.activeCatalog!.CatalogCode, userStore.currentUsername, 1]).toArray()
//       myData.value.forEach(art => {
//         art._FavoriteTags = allFavs.filter(fav => fav.Articles.indexOf(art.Id) >= 0)
//       })
//     }
//   }

//   const reloadArticles = async () => {
//     myData.value = await appConfig.DB!.getMyArticlesByCriteria(userStore.activeCatalog!, userStore.myAttributes!, userStore.currentUsername, criteria?.value || [], true, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet)
//   }

//   watch(() => criteria, async () => {
//     console.debug('criteria changed')
//     onLoading(true)
//     await reloadArticles()
//     onChanged(true)
//   }, { deep: true, immediate: true })

//   Dexie.on('storagemutated', listener)

//   onUnmounted(() => {
//     Dexie.on('storagemutated').unsubscribe(listener)
//   })

//   return myData
// }

export function useLiveArticlesCriteria(criteria: ComputedRef<FilterCriteria[] | undefined>, listContainer: Ref<HTMLElement | undefined>, onLoading: (fullReload: boolean) => void, onChanged: (fullReload: boolean) => void, doLoadMore: (count: number, increaseTotalVisible: boolean) => void, totalVisibleArts, availableArticlesToLoad) {
  // const myData = ref<MyArticle[]>([])
  // const myDataIndex = computed(() => myData.value.reduce((acc, curr, index) => {
  //   acc[curr.CatalogArticleId] = index
  //   return acc
  // }, {} as Record<string, number>))

  let maxUpdatedDate: Date | null = null

  const userStore = useUserStore()
  const notificationsStore = useNotificationStore()

  const browseByStore = useBrowseByStore()
  const articleStore = useArticleStore()
  const { t } = useI18n()

  const { onAppEvent, offAppEvent } = useEventsBus()
  const scrollPosition = ref<{ top: number, left: number }>()
  let applyFilterStartTime: number | null = null
  let applyBrowseByStartTime: number | null = null
  const isViewUnAssortedArticlesRestricted = userStore.activeCatalog?.DataSourceTypeId === appConstants.catalogTypes.inherited && !userStore.userProfile.isValidPrivilege(privileges.article.viewUnAssortedArticles)

  const getCatalogCodes = () => {
    const catalogCodes: number[] = []
    if (userStore.activeCatalog) {
      catalogCodes.push(userStore.activeCatalog.CatalogCode)
    }
    if (criteria.value) {
      const seasonIndex = criteria.value.findIndex(c => c.attribute === '_Seasons')
      if (seasonIndex >= 0 && criteria.value[seasonIndex].multipleVals) {
        criteria.value[seasonIndex].multipleVals?.forEach(val => catalogCodes.push(Number(val)))
      }
    }
    return catalogCodes
  }

  const loadResources = async () => {
    const catalogCodes = new Set(getCatalogCodes())

    const resources = await appConfig.DB!.resources.where('Articles').anyOf(Array.from(articleStore.articlesIndexMap.keys()))
      .filter(itm => catalogCodes.has(itm.CatalogCode) && itm.Status === 1)
      .toArray()
    resources.sort((a, b) => {
      const result = a.SortOrder - b.SortOrder
      if (result !== 0) {
        return result
      }
      else if (a.ResourceCategory.toString().trim().toLowerCase() + a.ResourceName.toString().trim().toLowerCase() < b.ResourceCategory.toString().trim().toLowerCase() + b.ResourceName.toString().trim().toLowerCase()) {
        return -1
      }
      else if (a.ResourceCategory.toString().trim().toLowerCase() + a.ResourceName.toString().trim().toLowerCase() > b.ResourceCategory.toString().trim().toLowerCase() + b.ResourceName.toString().trim().toLowerCase()) {
        return 1
      }
      else {
        return 0
      }
    })

    articleStore.articles.forEach((art) => {
      art._Resources = resources.filter(r => r.Articles.includes(art.Id))
    })
  }

  const loadFavs = async (articles: Article[], userId: number, currentCustomer?: LinkedCustomer) => {
    const catalogCodes = getCatalogCodes()

    const favoriteTags = await appConfig.DB!.myFavorites.where('[CatalogCode+UserId+Status+CustomerId]').anyOf(catalogCodes.map(catalogCode => [catalogCode, userId, 1, currentCustomer ? currentCustomer.CustomerId : -1])).toArray()
    const indexedFavoriteTags = favoriteTags.reduce<Record<string, FavoriteTag[]>>((acc, tag) => {
      tag.Articles?.forEach((articleId) => {
        const key = `${tag.CatalogCode}//${articleId}`
        if (!acc[key]) { acc[key] = [] }
        acc[key].push(tag)
      })
      return acc
    }, {})

    const sharedFavoriteTags = await appConfig.DB!.sharedFavorites.where('[CatalogCode+UserId]').anyOf(catalogCodes.map(catalogCode => [catalogCode, userId])).toArray()
    sharedFavoriteTags.forEach((sharedFavoriteTag) => {
      if (sharedFavoriteTag.Articles && sharedFavoriteTag.Articles.length) {
        sharedFavoriteTag.Articles.forEach((articleId) => {
          const key = `${sharedFavoriteTag.CatalogCode}//${articleId}`
          if (!indexedFavoriteTags.hasOwnProperty(key)) {
            indexedFavoriteTags[key] = []
          }
          indexedFavoriteTags[key].push(sharedFavoriteTag)
        })
      }
    })

    articles.forEach((art) => {
      const favoriteTagKey = `${art.CatalogCode}//${art.Id}`
      art._FavoriteTags = isDefined(indexedFavoriteTags[favoriteTagKey]) ? indexedFavoriteTags[favoriteTagKey].map(itm => new SimpleFavoriteTag(itm.Id, itm.Tag, itm.Color, itm.CreatedByUserName, itm.CustomerId, itm.SharedUsers, itm.SharedUsersGroups)) : []
    })
  }

  const reloadArticles = async (fullReload: boolean, _source?: string) => {
    // const eventSource: string = isDefined(source) && source !== '' ? source : ''

    if (!criteria?.value || criteria?.value.length === 0 || !userStore.activeCatalog) {
      articleStore.setArticles([])
      nextTick(() => {
        onChanged(true)
      })
      return
    }

    if (fullReload) {
      onLoading(true)
      maxUpdatedDate = null
    }

    let articles: Article[] = []

    // we will get segmented articles only
    let filterAtricles = false // if logged in as customer and no active segmentation do not call API
    if (!isDefined(userStore.currentCustomer) || (isDefined(userStore.currentCustomer) && isDefined(userStore.currentCustomerSegmentations) && userStore.currentCustomerSegmentations.Segmentations.length > 0)) {
      filterAtricles = true
    }
    if (criteria?.value.length && filterAtricles) {
      const filterAttributes = criteria?.value.map(criteria => criteria.attribute)
      const requestSourceCriteria = criteria?.value.find(criteria => criteria.attribute === '_RequestSource')
      let needToFilterCatalogArticles = false
      if (requestSourceCriteria) {
        needToFilterCatalogArticles = requestSourceCriteria.multipleVals?.some(item => [-1, -2].includes(item)) || false
      }
      if (isDefined(userStore.currentCustomer) && isDefined(userStore.currentCustomerSegmentations) && userStore.currentCustomerSegmentations.Segmentations.length > 0) {
        needToFilterCatalogArticles = true
      }
      const anyRequestFilter = filterAttributes.find(attribute => (userStore.myAttributes && userStore.myAttributes[attribute] && userStore.myAttributes[attribute].IsRequest))
      if (!anyRequestFilter || needToFilterCatalogArticles) {
        const filters = await Promise.all(criteria.value.map(async c => await c.toApiFilter(userStore.priceGroups)))
        if (isDefined(userStore.currentCustomer) && isDefined(userStore.currentCustomerSegmentations) && userStore.currentCustomerSegmentations.Segmentations.length > 0) {
          filters.unshift({
            attribute: '_CustomerSegmentations',
            mode: FilterCriteriaMode.multiString,
            value: userStore.currentCustomerSegmentations.Segmentations.filter(s => s.Status > 0).map(s => s.SegmentationId),
            exclude: false,
          })
        }
        for (let i = filters.length - 1; i >= 0; i--) {
          if (filters[i].attribute === '_RequestSource') {
            filters.splice(i, 1)
          }
        }
        const query = new URLSearchParams()
        const statusFilterIndex = filters.findIndex(f => f.attribute === 'Status')
        if (statusFilterIndex === -1) {
          query.set('status', 'true')
        }
        else if (filters[statusFilterIndex].value === -1) {
          filters.splice(statusFilterIndex, 1)
        }

        if (browseByStore.isBrowseByModel) {
          query.set('firstArticle', 'true')
        }
        if (!fullReload && maxUpdatedDate) {
          query.set('u', maxUpdatedDate.toISOString())
        }
        try {
          const res = net.t1v2.getNDJson(`/catalogs/${userStore.activeCatalog.CatalogCode}/articles/stream?${query.toString()}`, filters)
          for await (const record of res) {
            const catalogDetails = userStore.linkedCatalogDetails[record.CatalogCode] || userStore.activeCatalog
            const updatedDate = new Date(record.UpdatedOn || record.UpdatedDate)
            if (maxUpdatedDate == null || updatedDate > maxUpdatedDate) {
              maxUpdatedDate = updatedDate
            }
            const datasetAttributes = Object.values(userStore.myAttributes!).filter(attr => attr.IsDatasetAttribute)
            articles.push(new Article(catalogDetails.CatalogCode, catalogDetails.AssignedCatalogAttributes, record, catalogDetails.DataSourceTypeId, datasetAttributes))
          }
        }
        catch (e) {
          notificationsStore.addNotification({ message: t('general.unexpectedError'), type: 'Alert', details: utils.getErrorMessage(e), actions: ['ShowDetails', 'Support'] })
        }
      }
    }
    // articles = await appConfig.DB!.getArticlesByCriteria(userStore.activeCatalog, userStore.myAttributes!, criteria?.value || [], true, userStore.userProfile.Id, userStore.currentUsername, userStore.currentCustomer, userStore.currentCustomerSegmentations, browseByStore.browseByDetailsMatchCurrentCatalogOnlyFromCriteria, userStore.activeCatalog.RequestsEnabled > 0, fullReload ? null : lastDataLoad, eventSource)
    // articles = await appConfig.DB!.getModelsByCriteria(userStore.activeCatalog, userStore.myAttributes!, criteria?.value || [], true, userStore.userProfile.Id, userStore.currentUsername, userStore.currentCustomer, userStore.currentCustomerSegmentations, false, fullReload ? undefined : lastDataLoad)

    if (!browseByStore.isBrowseByModel && userStore.activeCatalog && userStore.myAttributes && userStore.activeCatalog.RequestsEnabled > 0) {
      const requestArticles = await appConfig.DB!.getRequestArticlesByCriteria(userStore.activeCatalog, userStore.myAttributes, criteria?.value || [], true)
      articles = articles.concat(requestArticles)
    }

    // show only active article if user doesnt have viewunassorted privilege
    if (isViewUnAssortedArticlesRestricted) {
      articles = articles.filter(article => article.Status === 1)
    }

    userStore.setTotalArticles(userStore.activeCatalog, criteria?.value)

    if (fullReload) {
      articleStore.setArticles([])
    }

    await loadFavs(articles, userStore.userProfile.Id, userStore.currentCustomer)

    // show only active article if user doesnt have viewunassorted privilege
    if (isViewUnAssortedArticlesRestricted) {
      articles = articles.filter(article => article.Status === 1)
    }
    const datasetAttributes = Object.values(userStore.myAttributes!).filter(attr => attr.IsDatasetAttribute)

    for (let index = 0; index < articles.length; index++) {
      const article = articles[index]
      let retailPrice: Price | null = null
      let wholesalePrice: Price | null = null
      let outletPrice: Price | null = null
      if (article.CatalogCode === userStore.activeCatalog?.CatalogCode) {
        if (userStore.priceGroups.retail) {
          retailPrice = article._Prices[userStore.priceGroups.retail.Id]
        }
        if (userStore.priceGroups.wholesale) {
          wholesalePrice = article._Prices[userStore.priceGroups.wholesale.Id]
        }
        if (userStore.priceGroups.outlet) {
          outletPrice = article._Prices[userStore.priceGroups.outlet.Id]
        }
      }
      else {
        const linkedCatalog = userStore.linkedCatalogDetails[article.CatalogCode]
        if (userStore.priceGroups.retail) {
          const linkedPriceGroup = linkedCatalog?._IndexedCatalogPriceGroupByLowercaseName[userStore.priceGroups.retail.Name.toString().trim().toLowerCase()]
          if (linkedPriceGroup) {
            retailPrice = article._Prices[linkedPriceGroup.Id]
          }
        }
        if (userStore.priceGroups.wholesale) {
          const linkedPriceGroup = linkedCatalog?._IndexedCatalogPriceGroupByLowercaseName[userStore.priceGroups.wholesale.Name.toString().trim().toLowerCase()]
          if (linkedPriceGroup) {
            wholesalePrice = article._Prices[linkedPriceGroup.Id]
          }
        }
        if (userStore.priceGroups.outlet) {
          const linkedPriceGroup = linkedCatalog?._IndexedCatalogPriceGroupByLowercaseName[userStore.priceGroups.outlet.Name.toString().trim().toLowerCase()]
          if (linkedPriceGroup) {
            outletPrice = article._Prices[linkedPriceGroup.Id]
          }
        }
      }

      const favoriteTags = article._FavoriteTags as SimpleFavoriteTag[]
      const myArticle = new MyArticle(article, userStore.activeCatalog!.AssignedCatalogAttributes, retailPrice?.Price || 0, wholesalePrice?.Price || 0, outletPrice?.Price || 0, [], true, [], favoriteTags, [], userStore.activeCatalog!.Season, userStore.activeCatalog!.DataSourceTypeId, userStore.activeCatalog!.Config.ArticlesCustomSortOrder, datasetAttributes)

      if (fullReload && articleStore.articles.length >= ARTICLES_LIMIT) {
        break
      }

      // if (fullReload) {
      //   // if (appConfig.DB!.articleMatchesCriteria(myArticle, userStore.myAttributes!, criteria?.value || [], true, true)) {
      //   articleStore.addArticle(myArticle)
      //   // }
      // }
      // else {
      //  if (appConfig.DB!.articleMatchesCriteria(myArticle, userStore.myAttributes!, criteria?.value || [], true, true)) {
      articleStore.addOrUpdateArticle(myArticle)
      //   }
      //   else {
      //      articleStore.deleteArticle(myArticle.CatalogArticleId)
      //   }
    //  }
    }

    // if (!fullReload) {
    //   // Assign updated favorites
    //   myData.value.forEach((art) => {
    //     art._FavoriteTags = favResponse.filter(fav => fav.Articles.includes(art.Id)).map(itm => new SimpleFavoriteTag(itm.Id, itm.Tag, itm.Color))
    //   })
    // }

    // sort articles
    articleStore.sortArticles()

    loadResources()
    // loadFavs()
    // When need to include allocations uncomment the below call
    // loadAllocations()

    onChanged(true)
  }

  // const loadAllocations = async () => {
  //   const allocations = await appConfig.DB!.allocations.where('Articles').anyOf(myData.value.map(art => art.Id)).toArray()
  //   myData.value.forEach((art) => {
  //     art._Allocations = allocations.filter(r => r.Articles.includes(art.Id))
  //   })
  // }

  watch(() => criteria, async (newCriteria) => {
    console.info('criteria changed', newCriteria)
    applyFilterStartTime = performance.now()
    await reloadArticles(true)
    if (applyFilterStartTime !== null && userStore.activeCatalog) {
      const loadTime = performance.now() - applyFilterStartTime
      appConfig.setApplicationInsights('Apply Filter', loadTime, userStore.activeCatalog?.CatalogCode, userStore.userProfile.UserName, criteria.value!)
      applyFilterStartTime = null
    }
  }, { deep: true, immediate: true })

  async function reloadArticlesWithPosition(fullReload: boolean = false, source: string) {
    scrollPosition.value = undefined
    if (listContainer.value) {
      scrollPosition.value = { top: listContainer.value.scrollTop, left: listContainer.value.scrollLeft }
    }
    console.info('reloadArticlesWithPosition', scrollPosition.value)
    await reloadArticles(fullReload, source)
  }

  async function onBrowseByChanged() {
    applyBrowseByStartTime = performance.now()
    await reloadArticles(true)
    if (applyBrowseByStartTime !== null && userStore.activeCatalog) {
      const loadTime = performance.now() - applyBrowseByStartTime
      appConfig.setApplicationInsights('Apply Browse By', loadTime, userStore.activeCatalog?.CatalogCode, userStore.userProfile.UserName, criteria.value!)
      applyBrowseByStartTime = null
    }
  }

  function scrollToNewArticle(newArticleId: string) {
    nextTick(() => {
      if (listContainer.value) {
        const articleElement = listContainer.value.querySelector(`[data-id="${newArticleId}"]`) as HTMLElement

        if (articleElement && articleElement !== null) {
          articleElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

          // highlight article
          articleElement.classList.add('highlight-article')

          // remove highlight after 5 seconds
          setTimeout(() => {
            articleElement.classList.remove('highlight-article')
          }, 5000)
        }
        else {
          if (availableArticlesToLoad.value !== 0) {
            doLoadMore(30, true)
            scrollToNewArticle(newArticleId)
          }
        }
      }
    })
  }

  function onCatalogDataUpdated(data: IDataUpdatedEvent) {
    reloadArticlesWithPosition(data.fullReload, data.source).then(async () => {
      // if new article created then scroll to the newly created article
      if (data && data.newArticleIds && data.newArticleIds.length > 0) {
        // check if the new articleid is present in the current view myData
        // if present we will load and scroll to new article
        let newArticleId = data.newArticleIds[0]
        let index = articleStore.articlesIndexMap.get(newArticleId.toString())
        if (browseByStore.isBrowseByModel) {
          // if new model
          for (const articleId of data.newArticleIds) {
            const newindex = articleStore.articlesIndexMap.get(articleId.toString())
            if (newindex !== -1) {
              newArticleId = articleId
              index = newindex
              break
            }
          }
          // // when model alredy rendered then for newArticleIds will not be part of the view, find out the rendered model and highlight it
          // if (index === -1 && userStore.activeCatalog) {
          //   const articles = await appConfig.DB!.getMyArticlesByCatalogArticleId(userStore.activeCatalog, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, newArticleId, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet)
          //   if (articles.length !== 0) {
          //     const articlesByModel = await appConfig.DB!.getMyArticlesByModelNumber(userStore.activeCatalog, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, articles[0].ModelNumber, true, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet)
          //     if (!(articlesByModel instanceof CancelToken) && articlesByModel) {
          //       const modelArticleIds = articlesByModel.map(art => art.CatalogArticleId)
          //       for (const articleId of modelArticleIds) {
          //         const newindex = myData.value.findIndex(art => art.CatalogArticleId === articleId.toString())
          //         if (newindex !== -1) {
          //           newArticleId = articleId
          //           index = newindex
          //           break
          //         }
          //       }
          //     }
          //   }
          // }
        }
        if (index !== -1) {
          nextTick(() => {
            scrollToNewArticle(newArticleId)
          })
        }
      }
    })
  }

  onMounted(() => {
    onAppEvent('catalogDataUpdated', onCatalogDataUpdated)
    onAppEvent('browseByChanged', onBrowseByChanged)
  })

  onUnmounted(() => {
    offAppEvent('catalogDataUpdated', onCatalogDataUpdated)
    offAppEvent('browseByChanged', onBrowseByChanged)
  })

  return {
    scrollPosition,
  }
}
